import React, { FC, useEffect, useState } from 'react'
import { Trans } from '@lingui/macro'
import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'
import {
  Box,
  DrawerItem,
  DrawerItemHeader,
  DrawerItemHint,
  Flex,
  Input,
  Select,
  Switch
} from 'front-end/src/components'
import { useTopicForEditQuery } from '../../../topic-edit-page/TopicEditPage'
import { usePatchDraftMutation } from './operations/patchDraft.codegen'
import IconCheck from '../../../../components/common/icon/IconCheck'
import { brandColors } from 'shared/basicPalette'
import IconReload from '../../../../components/common/icon/IconReload'
import { attemptLimitOptions } from 'front-end/src/pages/topic-detail-page/components/settings/SettingsHelpers'
import { TopicVersionTypeEnum } from 'types/TopicVersion'

export const SetQuizSettings: FC<{}> = () => {
  const { data, refetch: refetchTopic } = useTopicForEditQuery()
  const topic = data?.topic
  const isScorm = topic?.draftVersion?.topic_type === TopicVersionTypeEnum.SCORM
  const [passMark, setPassMark] = useState({
    value: 0
  })

  const getDefaultAttemptLimit = () => {
    if (topic?.draftVersion?.max_attempts) {
      return attemptLimitOptions.find(
        (item) => item.value === `${topic.draftVersion.max_attempts}`
      )
    }
    return attemptLimitOptions[0]
  }

  const [allowRetryValue, setAllowRetryValue] = useState(false)
  const defaultAttemptLimit = getDefaultAttemptLimit() || attemptLimitOptions[0]
  const [selectedAttemptLimit, setSelectedAttemptLimit] =
    useState<typeof defaultAttemptLimit>(defaultAttemptLimit)

  // Pass mark might be changed from outside during changing of quiz types.
  // eg. Changing single select quiz to free text quiz will decrease pass_mark if it was too high,
  // see method recalculateDraftNumberQuizzes
  useEffect(() => {
    if (topic?.__typename !== 'TopicModelAsEditor') {
      return
    }
    setPassMark({
      value: topic?.draftVersion?.pass_mark ?? 0
    })
    setAllowRetryValue(topic.draftVersion?.allow_retry || false)
  }, [topic])

  const [patchDraft, { loading }] = usePatchDraftMutation()

  if (!topic || topic.__typename !== 'TopicModelAsEditor') {
    return null // this should never occur
  } else {
    if (topic?.draftVersion?.number_quizzes === 0 && !isScorm) {
      return null
    }
  }

  const onAttemptChange = async (value) => {
    const maxAttemptsValue = Number(value.value)
    setSelectedAttemptLimit(value)
    await patchDraft({
      variables: {
        topicId: topic?.id,
        input: {
          max_attempts: maxAttemptsValue
        }
      }
    })
    await refetchTopic()
  }

  const allowedAllowRetry = (topic?.draftVersion?.pass_mark as number) > 0

  return (
    <>
      {!isScorm && (
        <DrawerItem isInline>
          <DrawerItemHeader mb={0}>
            <IconCheck />
            <Trans>Pass mark</Trans>
          </DrawerItemHeader>

          <Flex>
            <Box position="relative">
              {/* disabled property caused issue on firefox because it fires onChange [DEV-6049] */}
              <Input
                type="number"
                min="0"
                h="40px"
                w="80px"
                variant="filled"
                textAlign="center"
                pr="30px"
                max={topic?.draftVersion?.number_quizzes as number}
                value={passMark.value as number}
                data-test="PassMarkInput"
                onChange={async (e) => {
                  // replaces isDisabled property
                  if (loading) {
                    return
                  }
                  const value = parseInt(e.target.value)
                  try {
                    setPassMark({
                      value
                    })

                    await patchDraft({
                      variables: {
                        topicId: topic.id,
                        input: {
                          pass_mark: value,
                          allow_retry:
                            value > 0 ? topic?.draftVersion?.allow_retry : false
                        }
                      }
                    })
                    await refetchTopic()
                  } catch (e) {
                    setPassMark({
                      value: topic.draftVersion?.pass_mark ?? 0
                    })
                  }
                }}
              />
              <Flex
                position="absolute"
                right="8px"
                top={0}
                fontSize="14px"
                h="100%"
                align="center"
                color={brandColors.grey.mid}
              >
                / {topic?.draftVersion?.number_quizzes}
              </Flex>
            </Box>
          </Flex>
        </DrawerItem>
      )}

      <DrawerItem>
        <Flex align="center" justify="space-between">
          <DrawerItemHeader mb={0}>
            <IconReload />
            <Trans>Allow retry</Trans>
          </DrawerItemHeader>

          <Tooltip
            text={
              !allowedAllowRetry && !isScorm ? (
                <Trans>
                  Allow retry is only available when a pass mark is set to 1 or
                  higher.
                </Trans>
              ) : null
            }
          >
            <Switch
              dataTest="AllowRetrySwitch"
              isDisabled={(!allowedAllowRetry || loading) && !isScorm}
              isChecked={allowRetryValue}
              onChange={async (e) => {
                const isChecked = e.target.checked
                if (!allowedAllowRetry && !isScorm) return false
                setAllowRetryValue(isChecked)

                await patchDraft({
                  variables: {
                    topicId: topic?.id,
                    input: {
                      allow_retry: isChecked,
                      max_attempts: isChecked ? selectedAttemptLimit.value : 0
                    }
                  }
                })
                await refetchTopic()
              }}
            />
          </Tooltip>
        </Flex>

        <DrawerItemHint>
          <Trans>
            When enabled, users can retake quizzes in this resource as many
            times as they wish until they pass. Should they answer incorrectly,
            they will not be shown the correct answer.
          </Trans>
        </DrawerItemHint>

        {allowRetryValue && (
          <Box mt="8px">
            <Select
              options={attemptLimitOptions}
              onChange={onAttemptChange}
              value={selectedAttemptLimit}
            />
          </Box>
        )}
      </DrawerItem>
    </>
  )
}
